import React, { useCallback, useEffect, useState } from 'react';
import { api, ApiError, passkeyStepUp, explain, type AdminUser, type AuditEvent, type PendingInvite } from './authApi';

const fmt = (ts: number | null) => (ts ? new Date(ts).toLocaleString('pl-PL') : '—');
const btn = 'min-h-[44px] rounded-lg border border-brd bg-surface-card px-3 text-sm font-medium text-txt-primary hover:bg-accent-soft disabled:opacity-50';
// pl-3 + pr-10 (nie px-3): px nadpisywał padding @tailwindcss/forms i strzałka selecta wchodziła na tekst.
const field = 'min-h-[44px] rounded-lg border border-brd bg-surface-card pl-3 text-txt-primary';
const select = `${field} pr-10`;
const card = 'glass-pane rounded-2xl p-4';
const EVENT_PL: Record<string, string> = {
  'login.success': 'Logowanie', 'login.denied': 'Odmowa logowania', logout: 'Wylogowanie', 'invite.created': 'Utworzono zaproszenie', 'invite.revoked': 'Unieważniono zaproszenie',
  'invite.consumed': 'Zaproszenie wykorzystane', 'passkey.registered': 'Dodano passkey', 'passkey.removed': 'Usunięto passkey',
  'role.changed': 'Zmiana roli', 'user.blocked': 'Zablokowano', 'user.unblocked': 'Odblokowano', 'session.revoked': 'Wylogowano wszędzie',
  'admin.stepup': 'Potwierdzenie passkeyem', 'mode.changed': 'Zmiana trybu', 'stepup.denied': 'Nieudane potwierdzenie',
  'passkey.register_failed': 'Nieudana rejestracja passkeya',
};
type AuditKind = 'admin' | 'denied' | 'all';
const AUDIT_KINDS: { value: AuditKind; label: string }[] = [
  { value: 'admin', label: 'Logowania i administracja' },
  { value: 'denied', label: 'Odmowy logowania' },
  { value: 'all', label: 'Wszystkie' },
];

interface Props { onClose: () => void; selfEmail: string }

const AccessScreen: React.FC<Props> = ({ onClose, selfEmail }) => {
  const [users, setUsers] = useState<AdminUser[]>([]);
  const [events, setEvents] = useState<AuditEvent[]>([]);
  const [invites, setInvites] = useState<PendingInvite[]>([]);
  const [msg, setMsg] = useState('');
  const [busy, setBusy] = useState(false);
  const [invEmail, setInvEmail] = useState('');
  const [invRole, setInvRole] = useState<'diagnosta' | 'admin'>('diagnosta');
  const [link, setLink] = useState('');
  // Denials are aggregated server-side and shown separately, so a flood of failed logins never hides admin events.
  const [auditKind, setAuditKind] = useState<AuditKind>('admin');

  const load = useCallback(async () => {
    try {
      const [u, a, i] = await Promise.all([
        api('GET', '/api/auth/admin/users'), api('GET', `/api/auth/admin/audit?kind=${auditKind}`), api('GET', '/api/auth/admin/invites').catch(() => null), // list is optional: never blocks the rest
      ]);
      setUsers(u.users); setEvents(a.events); setInvites(i?.invites ?? []);
    } catch (e) { setMsg(explain(e)); }
  }, [auditKind]);
  useEffect(() => { load(); }, [load]);

  /** Runs an admin action; on STEP_UP_REQUIRED asks for Face ID / Touch ID and retries once. */
  const act = async <T,>(fn: () => Promise<T>): Promise<T | undefined> => {
    setBusy(true); setMsg('');
    try {
      try { return await fn(); }
      catch (e) {
        if (e instanceof ApiError && e.code === 'STEP_UP_REQUIRED') {
          setMsg('Potwierdź Face ID / Touch ID, aby kontynuować…');
          await passkeyStepUp();
          setMsg('');
          return await fn();
        }
        throw e;
      }
    } catch (e) { setMsg(explain(e)); return undefined; }
    finally { setBusy(false); }
  };

  const post = (path: string, body: unknown = {}) => act(async () => { const r = await api('POST', path, body); await load(); return r; });

  const invite = async () => {
    const r = await act(() => api('POST', '/api/auth/admin/invites', { email: invEmail.trim(), role: invRole }));
    if (r) { setLink(r.link); setInvEmail(''); load(); }
  };
  const copy = async () => { try { await navigator.clipboard.writeText(link); setMsg('Link skopiowany.'); } catch { setMsg('Zaznacz link i skopiuj ręcznie.'); } };
  const share = async () => { try { await navigator.share({ title: 'Zaproszenie do WISC-V', url: link }); } catch { /* cancelled */ } };

  return (
    <div className="app-canvas fixed inset-0 z-page overflow-y-auto" role="dialog" aria-modal="true" aria-label="Dostępy">
      <div className="mx-auto max-w-3xl p-3 pb-24 sm:p-4">
        {/* Pasek tytułu przyklejony u góry — „Zamknij” zawsze pod ręką przy długich listach */}
        <div className="glass-bar sticky top-2 z-header flex items-center justify-between gap-3 rounded-2xl px-4 py-2">
          <h1 className="text-xl font-semibold text-txt-primary">Dostępy</h1>
          <button type="button" className={btn} onClick={onClose}>Zamknij</button>
        </div>
        {msg && <p role="status" className="mt-3 rounded-lg bg-accent-soft p-3 text-sm text-txt-primary">{msg}</p>}

        <section className={`mt-6 ${card}`}>
          <h2 className="text-base font-semibold text-txt-primary">Zaproś osobę</h2>
          <div className="mt-3 flex flex-col gap-2 sm:flex-row">
            <input type="email" inputMode="email" autoCapitalize="none" value={invEmail} onChange={e => setInvEmail(e.target.value)}
              placeholder="adres e-mail" aria-label="Adres e-mail"
              className={`${field} min-w-0 flex-1 pr-3 text-base`} />
            <select value={invRole} onChange={e => setInvRole(e.target.value as any)} aria-label="Rola"
              className={`${select} text-base`}>
              <option value="diagnosta">Diagnosta</option><option value="admin">Administrator</option>
            </select>
            <button type="button" className={btn} disabled={busy || !invEmail} onClick={invite}>Utwórz link</button>
          </div>
          {link && (
            <div className="mt-3">
              <p className="text-xs text-txt-secondary">Link jednorazowy, ważny 24 godziny. Wyślij go wyłącznie zaproszonej osobie.</p>
              <input readOnly value={link} onFocus={e => e.currentTarget.select()} aria-label="Link zaproszenia"
                className={`${field} mt-1 w-full pr-3 text-sm`} />
              <div className="mt-2 flex gap-2">
                <button type="button" className={btn} onClick={copy}>Kopiuj</button>
                {typeof navigator.share === 'function' && <button type="button" className={btn} onClick={share}>Udostępnij</button>}
              </div>
            </div>
          )}
        </section>

        {invites.length > 0 && (
          <section className={`mt-6 ${card}`}>
            <h2 className="text-base font-semibold text-txt-primary">Oczekujące zaproszenia</h2>
            <ul className="mt-2 divide-y divide-brd text-sm">
              {invites.map(i => (
                <li key={i.id} className="flex flex-wrap items-center justify-between gap-2 py-2">
                  <span className="min-w-0">
                    <span className="break-all font-medium text-txt-primary">{i.email}</span>{' '}
                    <span className="text-xs text-txt-secondary">· {i.role === 'admin' ? 'Administrator' : 'Diagnosta'} · ważne do {fmt(i.expiresAt)}</span>
                  </span>
                  <button type="button" className={btn} disabled={busy}
                    aria-label={`Unieważnij zaproszenie: ${i.email}`}
                    onClick={() => { if (window.confirm(`Unieważnić zaproszenie dla ${i.email}? Link przestanie działać.`)) post('/api/auth/admin/invites/revoke', { id: i.id }); }}>
                    Unieważnij
                  </button>
                </li>
              ))}
            </ul>
          </section>
        )}

        <section className="mt-6 space-y-3">
          <h2 className="text-base font-semibold text-txt-primary">Osoby</h2>
          {users.map(u => (
            <div key={u.id} className={card}>
              <div className="flex flex-wrap items-center justify-between gap-2">
                <div className="min-w-0">
                  <p className="break-all font-medium text-txt-primary">{u.email}{u.email === selfEmail ? ' (Ty)' : ''}</p>
                  <p className="text-xs text-txt-secondary">
                    Ostatnie logowanie: {fmt(u.lastLoginAt)} · passkeyów: {u.passkeyCount}
                  </p>
                </div>
                <span className={`rounded-full border px-3 py-1 text-xs font-medium ${u.status === 'active' ? 'border-accent-border bg-accent-soft text-txt-primary' : 'status-blocked'}`}>
                  {u.status === 'active' ? 'Aktywny' : 'Zablokowany'}
                </span>
              </div>
              <div className="mt-3 flex flex-wrap items-center gap-2">
                <select value={u.role} aria-label={`Rola: ${u.email}`} disabled={busy}
                  onChange={e => post(`/api/auth/admin/users/${u.id}/role`, { role: e.target.value })}
                  className={`${select} text-sm`}>
                  <option value="diagnosta">Diagnosta</option><option value="admin">Administrator</option>
                </select>
                {u.status === 'active'
                  ? <button type="button" className={btn} disabled={busy} onClick={() => post(`/api/auth/admin/users/${u.id}/block`)}>Zablokuj</button>
                  : <button type="button" className={btn} disabled={busy} onClick={() => post(`/api/auth/admin/users/${u.id}/unblock`)}>Odblokuj</button>}
                <button type="button" className={btn} disabled={busy} onClick={() => post(`/api/auth/admin/users/${u.id}/logout-all`)}>Wyloguj wszędzie</button>
              </div>
              {u.passkeys.length > 0 && (
                <ul className="mt-3 divide-y divide-brd text-sm">
                  {u.passkeys.map(k => (
                    <li key={k.id} className="flex items-center justify-between gap-2 py-2">
                      <span className="text-txt-primary">{k.name} <span className="text-xs text-txt-secondary">· użyty {fmt(k.lastUsedAt)}</span></span>
                      <button type="button" className={btn} disabled={busy}
                        onClick={() => { if (window.confirm(`Usunąć passkey „${k.name}”?`)) post('/api/auth/admin/passkeys/delete', { id: k.id }); }}>
                        Usuń
                      </button>
                    </li>
                  ))}
                </ul>
              )}
            </div>
          ))}
        </section>

        <section className={`mt-6 ${card}`}>
          <div className="flex flex-wrap items-center justify-between gap-2">
            <h2 className="text-base font-semibold text-txt-primary">Dziennik (ostatnie 100 wpisów)</h2>
            <select value={auditKind} onChange={e => setAuditKind(e.target.value as AuditKind)} aria-label="Rodzaj zdarzeń"
              className={`${select} text-sm`}>
              {AUDIT_KINDS.map(k => <option key={k.value} value={k.value}>{k.label}</option>)}
            </select>
          </div>
          {events.length === 0 && <p className="mt-2 text-sm text-txt-secondary">Brak zdarzeń.</p>}
          <ul className="mt-2 divide-y divide-brd text-sm">
            {events.map(e => {
              const count = Number(e.meta?.count) || 1;
              return (
                <li key={e.id} className="py-2">
                  <span className="text-xs text-txt-secondary">{fmt(e.ts)}</span>{' '}
                  <span className="font-medium text-txt-primary">{EVENT_PL[e.event] || e.event}{count > 1 ? ` ×${count}` : ''}</span>{' '}
                  <span className="break-all text-txt-secondary">{e.user}{e.meta?.target ? ` → ${String(e.meta.target)}` : ''}{e.meta?.reason ? ` (${String(e.meta.reason)})` : ''}{e.event === 'login.denied' && e.meta?.ip ? ` · IP ${String(e.meta.ip)}` : ''}</span>
                </li>
              );
            })}
          </ul>
        </section>
      </div>
    </div>
  );
};

export default AccessScreen;
